import { Component, computed, effect, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouteCodeEnum } from '@models/menu';
import { DisplayType } from '@models/module/fields/enums';
import {
  AddressModel,
  DefaultAddress,
  getAddressModelFromAzureMapAddressAndPosition,
} from '@models/module/fields/main';
import { Permission } from '@models/permission';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateModule } from '@ngx-translate/core';
import { AzureMapComponent } from '@shared/components/azure-map/azure-map.component';
import { AzureMapPosition } from '@shared/components/azure-map/azure-map.model';
import { AzureMapService } from '@shared/components/azure-map/azure-map.service';
import { ConfirmationDialog, CipoDialogHeaderComponent } from '@shared/dialogs';
import { UserStore } from '@signal-store/index';
import { NotFoundComponent } from '@system/not-found/not-found.component';
import { CipoAddressFormComponent } from '../cipo-address-form/cipo-address-form.component';
import { CipoAddressService, SyncGlobalAddressesRequest } from '../cipo-address.service';

export type CipoAddressDialogData = {
  addresses: AddressModel[];
  editMode: boolean;
  multiple: boolean;
  label: string;
  displayType: DisplayType;
};

@Component({
  selector: 'cipo-address-dialog',
  templateUrl: './cipo-address-dialog.component.html',
  styleUrls: ['./cipo-address-dialog.component.scss'],
  imports: [
    FormsModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    TranslateModule,
    MatDialogModule,
    MatButtonModule,
    MatDividerModule,
    MatListModule,
    MatTooltipModule,
    FlexLayoutModule,
    CipoAddressFormComponent,
    NotFoundComponent,
    AzureMapComponent,
    CipoDialogHeaderComponent,
  ],
})
export class CipoAddressDialogComponent implements OnInit {
  private dialog = inject(MatDialog);
  readonly dialogRef = inject<MatDialogRef<CipoAddressDialogComponent>>(MatDialogRef);
  private addressData = inject<CipoAddressDialogData>(MAT_DIALOG_DATA);
  azureMapService = inject(AzureMapService);
  addressService = inject(CipoAddressService);
  private userStore = inject(UserStore);

  label = signal(this.addressData.label);
  editMode = signal(this.addressData.editMode);
  multiple = signal(this.addressData.multiple);
  displayType = signal(this.addressData.displayType);

  addresses = signal<AddressModel[]>(this.addressData.addresses);
  address = signal<AddressModel>(this.addressData.addresses[0]);
  addressIndex = signal<number>(0);
  changed = signal<boolean>(false);
  /** Ids of previously-persisted global addresses that were demoted in this session. */
  deletedLocationIds = signal<number[]>([]);
  globalAddresses = signal<AddressModel[]>([]);
  globalSearchTerm = signal<string>('');

  private listsModule = computed(() => this.userStore.getModuleByCode(RouteCodeEnum.ListsManagement));

  /** True when the current user has Create permission on the Lists Management module. */
  canCreateGlobalAddress = computed(() => !!this.listsModule()?.operations?.find(op => op.id === Permission.Create));

  /** True when the current user has Update permission on the Lists Management module. */
  canUpdateGlobalAddresses = computed(() => !!this.listsModule()?.operations?.find(op => op.id === Permission.Update));

  /** True when the current user has Delete permission on the Lists Management module. */
  canDeleteGlobalAddress = computed(() => !!this.listsModule()?.operations?.find(op => op.id === Permission.Delete));

  coordinates = computed<AzureMapPosition[]>(() => this.addresses().map(addr => ({ lat: addr.lat, lon: addr.lon })));

  /** Global addresses filtered in-memory by the search term. */
  filteredGlobalAddresses = computed<AddressModel[]>(() => {
    const term = this.globalSearchTerm().toLowerCase().trim();
    if (!term) return this.globalAddresses();
    return this.globalAddresses().filter(
      g => g.locationName?.toLowerCase().includes(term) || g.address?.toLowerCase().includes(term),
    );
  });

  isValid = computed(() => this.addresses().every(addr => this.addressService.addressIsValid(addr)));
  canSave = computed(() => this.changed() && (this.isValid() || this.deletedLocationIds().length > 0));
  currentAddressIsValid = computed(
    () => !this.addresses().length || this.addressService.addressIsValid(this.addresses()[this.addressIndex()]),
  );

  addressIndexChangedEffect = effect(() => {
    this.address.set(this.addresses()[this.addressIndex()]);
  });

  ngOnInit(): void {
    if (!this.editMode()) {
      return;
    }
    this.addressService.getGlobalAddresses().subscribe(addresses => this.globalAddresses.set(addresses));
  }

  /**
   * Opens the global addresses management dialog. On save, refreshes the global list and
   * removes from the regular list any addresses that are no longer global.
   */
  openGlobalAddressesDialog(): void {
    this.addressService.openGlobalAddressesDialog(this.globalAddresses()).subscribe(refreshed => {
      if (!refreshed) {
        return;
      }
      this.globalAddresses.set(refreshed);
      // if a currently selected regular address was demoted, clear its global badge
      const globalIds = new Set(refreshed.map(g => g.locationId));
      this.addresses.update(current =>
        current.map(a => (a.isGlobal && a.locationId && !globalIds.has(a.locationId) ? { ...a, isGlobal: false } : a)),
      );
    });
  }

  /**
   * Promotes a regular address to global by adding it to the global list and syncing on save.
   */
  promoteToGlobal(index: number): void {
    const addr = this.addresses()[index];
    if (!addr || !this.addressService.addressIsValid(addr)) {
      return;
    }
    // mark as global in the regular list so it gets synced on save
    this.addresses.update(current => current.map((a, i) => (i === index ? { ...a, isGlobal: true } : a)));
    this.address.set(this.addresses()[this.addressIndex()]);
    this.changed.set(true);
  }

  /**
   * Removes a global address from the global list after user confirmation.
   * Tracks the deletion so it is synced on save, and demotes any matching
   * regular address so it is not re-upserted as global during the same save.
   */
  deleteGlobalAddress(globalAddress: AddressModel): void {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'MODULE_DEF_ADDRESS.DELETE_GLOBAL_TITLE',
        messages: ['MODULE_DEF_ADDRESS.DELETE_GLOBAL_MESSAGE'],
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        return;
      }
      this.globalAddresses.update(current => current.filter(a => a.locationId !== globalAddress.locationId));
      // if this was a persisted global address, track for deletion on save
      if (globalAddress.locationId) {
        this.deletedLocationIds.update(ids => [...ids, globalAddress.locationId]);
      }
      // demote any matching address in the regular list so it is not re-upserted as global during save
      this.addresses.update(current =>
        current.map(a => (a.isGlobal && a.locationId === globalAddress.locationId ? { ...a, isGlobal: false } : a)),
      );
      this.changed.set(true);
    });
  }

  /**
   * Selects a global address into the address form.
   * In single mode, replaces the current entry; in multiple mode, appends a new entry
   * (or navigates to an existing one if the same locationId is already in the list).
   */
  selectGlobalAddress(globalAddress: AddressModel): void {
    const existingIndex = this.addresses().findIndex(a => a.locationId === globalAddress.locationId);
    if (existingIndex >= 0) {
      this.setAddress(existingIndex);
      return;
    }
    if (this.multiple()) {
      this.addresses.update(current => [...current, { ...globalAddress }]);
      this.addressIndex.set(this.addresses().length - 1);
    } else {
      this.addresses.set([{ ...globalAddress }]);
      this.addressIndex.set(0);
    }
    this.changed.set(true);
  }

  addressChanged(updatedAddress: AddressModel) {
    const updatedAddresses = [...this.addresses()];
    const oldAddress = updatedAddresses[this.addressIndex()];

    updatedAddresses[this.addressIndex()] = updatedAddress;
    this.addresses.set(updatedAddresses);
    this.changed.set(true);

    if (
      oldAddress &&
      updatedAddress &&
      (oldAddress.lat !== updatedAddress.lat || oldAddress.lon !== updatedAddress.lon)
    ) {
      // address coordinates have changed, run the reverse geocoding to update the address fields
      this.handleCoordinateChanged(updatedAddress);
    }
  }

  handleCoordinateChanged(position: AzureMapPosition) {
    if (!this.editMode() || !position) {
      return;
    }
    const { lat, lon } = position;
    if (!lat || !lon) {
      return;
    }
    this.azureMapService.getAddressFromCoordinates(lat, lon).subscribe({
      next: addressResult => {
        if (
          !addressResult ||
          !addressResult.addresses ||
          addressResult.addresses.length === 0 ||
          !addressResult.addresses[0].address
        ) {
          return;
        }

        // take the first result and update the address form
        const { address } = addressResult.addresses[0];
        this.address.set({
          // update address model with reverse geocoded address and new coordinates
          ...getAddressModelFromAzureMapAddressAndPosition(address, position),
          // preserve location id and name if already set
          locationId: this.address()?.locationId,
          locationName: this.address()?.locationName,
        });
        this.addressChanged(this.address());
      },
      error: () => {
        // only update coordinates if reverse geocoding fails
        this.address.update(addr => ({ ...addr, lat, lon }));
        this.addressChanged(this.address());
      },
    });
  }

  handleCoordinatesChanged(coordinates: AzureMapPosition[]) {
    if (!this.editMode()) {
      return;
    }
    this.handleCoordinateChanged(coordinates[this.addressIndex()]);
  }

  setAddress(index: number) {
    if (this.editMode() && !this.currentAddressIsValid()) {
      return;
    }
    this.addressIndex.set(index);
  }

  addAddress() {
    if (!this.currentAddressIsValid()) {
      return;
    }
    // add a new default address and set the index to the new address
    this.addresses.update(addresses => [...addresses, { ...DefaultAddress }]);
    this.addressIndex.set(this.addresses().length - 1);
  }

  deleteAddress(index: number) {
    const dialogRef = this.dialog.open(ConfirmationDialog, {
      data: {
        title: 'MODULE_DEF_ADDRESS.DELETE_TITLE',
        messages: ['MODULE_DEF_ADDRESS.DELETE_MESSAGE'],
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (!!result) {
        const currentAddresses = this.addresses();

        currentAddresses.splice(index, 1);
        this.addresses.set([...currentAddresses]);

        // update index to previous or next address
        const newIndex = index >= this.addresses().length ? index - 1 : index;
        this.addressIndex.set(newIndex);

        // ensure address form is updated after deletion, new index might be the same as previous one
        this.address.set(this.addresses()[newIndex]);

        this.changed.set(true);
      }
    });
  }

  save() {
    // only close with valid regular addresses
    const validAddresses = this.addresses().filter(a => this.addressService.addressIsValid(a));
    const promoted = validAddresses.filter(a => a.isGlobal);
    const hasGlobalChanges = promoted.length > 0 || this.deletedLocationIds().length > 0;

    if (!hasGlobalChanges) {
      this.dialogRef.close(validAddresses);
      return;
    }

    // sync global address promotions/demotions, then close
    const request: SyncGlobalAddressesRequest = {
      upserted: promoted.map(a => ({ ...a, isGlobal: true })),
      deletedLocationIds: this.deletedLocationIds(),
    };
    this.addressService.syncGlobalAddresses(request).subscribe(refreshed => {
      this.globalAddresses.set(refreshed);
      this.dialogRef.close(validAddresses);
    });
  }
}
